import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { GraduationCap, Plane, Layers, ChevronLeft, Award, Target, Globe, ChevronDown, Heart, Home, AlertTriangle, Loader2, ShieldAlert, Camera } from 'lucide-react';
import SelfieVerificationModal from './SelfieVerificationModal';
import { usePlatformFlags } from '@/hooks/usePlatformFlags';

const FLAG_EMOJI = {
  Australia: '\u{1F1E6}\u{1F1FA}', Canada: '\u{1F1E8}\u{1F1E6}', 'United Kingdom': '\u{1F1EC}\u{1F1E7}',
  'United States': '\u{1F1FA}\u{1F1F8}', 'New Zealand': '\u{1F1F3}\u{1F1FF}', Germany: '\u{1F1E9}\u{1F1EA}',
  Ireland: '\u{1F1EE}\u{1F1EA}', Singapore: '\u{1F1F8}\u{1F1EC}',
};

export default function IntentPopup({ onClose, preSelected: initialSelected = null }) {
  const navigate = useNavigate();
  const { isEnabled } = usePlatformFlags();
  const [selected, setSelected] = useState(initialSelected);
  const [destination, setDestination] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [limitError, setLimitError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showSelfieModal, setShowSelfieModal] = useState(false);

  useEffect(() => {
    axios.get(`${API}/visa-db/countries`).then(r => setDestinations(r.data || [])).catch(() => {});
  }, []);

  const allOptions = [
    { id: 'admission', label: 'Change Course', icon: GraduationCap, flagKey: 'courses' },
    { id: 'visa', label: 'Visitor Visa', icon: Plane, flagKey: 'visa' },
    { id: 'study_visa', label: 'Study Visa', icon: Layers, flagKey: 'visa' },
    { id: 'temp_visa', label: 'Temp Visa', icon: Plane, flagKey: 'visa' },
    { id: 'pr_visa', label: 'PR Visa', icon: Globe, flagKey: 'visa' },
    { id: 'rpl', label: 'RPL', icon: Award, flagKey: 'rpl' },
    { id: 'skills', label: 'Skills', icon: Target, flagKey: 'skills' },
    { id: 'insurance', label: 'Insurance', icon: Heart, flagKey: 'insurance' },
    { id: 'accommodation', label: 'Accommodation', icon: Home, flagKey: 'rentals' },
  ];
  const options = allOptions.filter(o => isEnabled(o.flagKey));

  const handleDismiss = () => {
    localStorage.setItem('unexa_intent_shown', 'true');
    onClose();
  };

  const proceedToRoute = async () => {
    localStorage.setItem('unexa_intent', selected);
    localStorage.setItem('unexa_intent_shown', 'true');

    if (selected === 'admission' || selected === 'study_visa' || selected === 'rpl' || selected === 'skills' || selected === 'temp_visa' || selected === 'pr_visa' || selected === 'insurance') {
      localStorage.setItem('express_product_type', selected);
    }

    // Carry the preferred agent (if user came from an agent profile)
    const preferredAgent = localStorage.getItem('preferred_agent_id') || '';
    if (preferredAgent) {
      // Lock the agent assignment on the student record so the downstream
      // application creation auto-picks this agent. Idempotent — backend
      // returns 400 if already assigned (fine).
      try {
        await axios.post(`${API}/agents/${preferredAgent}/choose`);
      } catch (e) {
        if (e.response?.status && ![400, 403].includes(e.response.status)) {
          // 401 (auth not loaded yet), 404, 500 — surface but don't block
          console.warn('agent choose failed:', e.response?.status);
        }
      }
    }
    const withAgent = (path) => preferredAgent ? `${path}${path.includes('?') ? '&' : '?'}agent=${preferredAgent}` : path;

    if (selected === 'visa' && destination) {
      localStorage.setItem('visa_express_destination', destination);
      onClose();
      navigate(withAgent('/apply/visa-express/passport'));
      return;
    }

    onClose();
    const routes = {
      admission: '/apply/admission-express/info',
      study_visa: '/apply/admission-express/info',
      visa: '/apply/visa-setup',
      rpl: '/apply/rpl-express/info',
      skills: '/apply/skills-express/info',
      temp_visa: '/apply/temp-visa-express/info',
      pr_visa: '/apply/pr-visa-express/info',
      insurance: '/apply/insurance-express/info',
      accommodation: '/rentals',
    };
    navigate(withAgent(routes[selected] || '/flow'));
  };

  const handleContinue = async () => {
    if (!selected) return;

    // Accommodation doesn't need abuse check
    if (selected === 'accommodation') {
      proceedToRoute();
      return;
    }

    setChecking(true);
    setLimitError(null);

    try {
      const res = await axios.post(`${API}/workflow/check-limits`, { type: selected });
      const data = res.data;

      if (data.selfie_required) {
        setChecking(false);
        setShowSelfieModal(true);
        return;
      }

      if (!data.allowed) {
        const err = data.errors?.[0];
        setLimitError(err || { code: 'BLOCKED', message: 'You cannot create this application right now.' });
        setChecking(false);
        return;
      }

      // Allowed — proceed
      setChecking(false);
      proceedToRoute();
    } catch (e) {
      // If the check endpoint fails, still allow (graceful degradation)
      console.warn('Abuse check failed, proceeding:', e);
      setChecking(false);
      proceedToRoute();
    }
  };

  const handleSelfieSubmitted = () => {
    setShowSelfieModal(false);
    setLimitError({
      code: 'SELFIE_PENDING',
      message: 'Your identity verification is under review. Please wait for approval before creating new applications.',
    });
  };

  const isVisaSelected = selected === 'visa';
  const canContinue = selected && (!isVisaSelected || destination);

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="intent-popup">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleDismiss} />
        <div className="relative bg-white rounded-3xl shadow-2xl w-[90%] max-w-sm p-6 animate-scale-in" data-testid="intent-popup-card">
          <div className="flex items-center mb-6">
            <button onClick={handleDismiss} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors" data-testid="intent-back-btn">
              <ChevronLeft className="h-4 w-4 text-slate-600" />
            </button>
            <h2 className="text-lg font-bold text-[#0A2540] flex-1 text-center pr-8">Select Your Journey</h2>
          </div>

          <div className="space-y-3 mb-5" data-testid="intent-options">
            <div className="flex items-center justify-center gap-3">
              {options.slice(0, 3).map((opt) => (
                <OptionCard key={opt.id} opt={opt} selected={selected} onSelect={(id) => { setSelected(id); setLimitError(null); if (id !== 'visa') setDestination(''); }} />
              ))}
            </div>
            <div className="flex items-center justify-center gap-3">
              {options.slice(3, 6).map((opt) => (
                <OptionCard key={opt.id} opt={opt} selected={selected} onSelect={(id) => { setSelected(id); setLimitError(null); if (id !== 'visa') setDestination(''); }} />
              ))}
            </div>
            <div className="flex items-center justify-center gap-3">
              {options.slice(6).map((opt) => (
                <OptionCard key={opt.id} opt={opt} selected={selected} onSelect={(id) => { setSelected(id); setLimitError(null); if (id !== 'visa') setDestination(''); }} />
              ))}
            </div>
          </div>

          {/* Destination picker when Visitor Visa is selected */}
          {isVisaSelected && (
            <div className="mb-5 animate-slide-down" data-testid="visa-destination-section">
              <label className="text-[11px] font-semibold text-slate-500 mb-1.5 block flex items-center gap-1">
                <Globe className="h-3 w-3" /> Where are you going?
              </label>
              <div className="relative">
                {destination && FLAG_EMOJI[destination] && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg z-10">{FLAG_EMOJI[destination]}</span>
                )}
                <select
                  value={destination}
                  onChange={e => setDestination(e.target.value)}
                  className={`w-full py-3 rounded-xl bg-slate-50 border-2 text-sm font-medium text-slate-700 focus:border-[#2F80ED] outline-none transition-colors appearance-none cursor-pointer ${destination ? 'border-[#2F80ED] bg-blue-50/30' : 'border-slate-200'} ${destination && FLAG_EMOJI[destination] ? 'pl-10 pr-10' : 'pl-3.5 pr-10'}`}
                  data-testid="visa-destination-select"
                >
                  <option value="">Select destination</option>
                  {destinations.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Limit Error / Warning Banner */}
          {limitError && (
            <div className="mb-4 animate-slide-down" data-testid="limit-error-banner">
              <div className={`p-3.5 rounded-2xl border ${
                limitError.code === 'SELFIE_PENDING' ? 'bg-amber-50 border-amber-200' :
                limitError.code === 'ACCOUNT_SUSPENDED' ? 'bg-red-50 border-red-200' :
                'bg-rose-50 border-rose-200'
              }`}>
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                    limitError.code === 'SELFIE_PENDING' ? 'bg-amber-100' :
                    limitError.code === 'ACCOUNT_SUSPENDED' ? 'bg-red-100' :
                    'bg-rose-100'
                  }`}>
                    {limitError.code === 'SELFIE_PENDING' ? (
                      <Camera className="h-4 w-4 text-amber-600" />
                    ) : limitError.code === 'ACCOUNT_SUSPENDED' ? (
                      <ShieldAlert className="h-4 w-4 text-red-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-rose-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold ${
                      limitError.code === 'SELFIE_PENDING' ? 'text-amber-800' :
                      limitError.code === 'ACCOUNT_SUSPENDED' ? 'text-red-800' :
                      'text-rose-800'
                    }`}>
                      {limitError.code === 'LIMIT_REACHED' ? 'Application Limit Reached' :
                       limitError.code === 'SELFIE_PENDING' ? 'Verification Pending' :
                       limitError.code === 'PASSPORT_DUPLICATE' ? 'Passport Already In Use' :
                       limitError.code === 'BENEFICIARY_LIMIT' ? 'Beneficiary Limit Reached' :
                       limitError.code === 'ACCOUNT_SUSPENDED' ? 'Account Suspended' :
                       'Cannot Proceed'}
                    </p>
                    <p className={`text-[11px] mt-0.5 ${
                      limitError.code === 'SELFIE_PENDING' ? 'text-amber-600' :
                      limitError.code === 'ACCOUNT_SUSPENDED' ? 'text-red-600' :
                      'text-rose-600'
                    }`}>{limitError.message}</p>
                    {limitError.code === 'LIMIT_REACHED' && limitError.active_app_id && (
                      <button
                        onClick={() => { onClose(); navigate(`/applications/${limitError.active_app_id}`); }}
                        className="mt-2 px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-[10px] font-bold text-rose-700 hover:bg-rose-50 transition-colors"
                        data-testid="view-active-app-btn"
                      >
                        View Active Application
                      </button>
                    )}
                    {limitError.code === 'BENEFICIARY_LIMIT' && (
                      <button
                        onClick={() => { onClose(); navigate('/profile'); }}
                        className="mt-2 px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-[10px] font-bold text-rose-700 hover:bg-rose-50 transition-colors"
                        data-testid="register-agent-btn"
                      >
                        Register as Agent
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleContinue}
            disabled={!canContinue || checking}
            data-testid="intent-continue-btn"
            className={`w-full py-3.5 rounded-2xl font-bold text-sm transition-all duration-200 ${
              canContinue && !checking
                ? 'bg-[#2F80ED] text-white hover:bg-[#1A6BD4] active:scale-[0.98] shadow-lg shadow-blue-200'
                : 'bg-slate-100 text-slate-300 cursor-not-allowed'
            }`}
          >
            {checking ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking...
              </span>
            ) : 'Continue'}
          </button>
        </div>

        <style>{`
          @keyframes scaleIn {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-scale-in { animation: scaleIn 0.25s ease-out; }
          @keyframes slideDown {
            0% { opacity: 0; transform: translateY(-8px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-slide-down { animation: slideDown 0.2s ease-out; }
        `}</style>
      </div>

      {showSelfieModal && (
        <SelfieVerificationModal
          onClose={() => setShowSelfieModal(false)}
          onSubmitted={handleSelfieSubmitted}
        />
      )}
    </>
  );
}

function OptionCard({ opt, selected, onSelect }) {
  const isActive = selected === opt.id;
  return (
    <button
      onClick={() => onSelect(opt.id)}
      data-testid={`intent-${opt.id}`}
      className="flex flex-col items-center group"
    >
      <div className={`w-[72px] h-[72px] rounded-2xl flex items-center justify-center transition-all duration-200 ${
        isActive
          ? 'bg-blue-50 border-2 border-[#2F80ED] shadow-lg shadow-blue-100'
          : 'bg-slate-50 border-2 border-transparent hover:border-slate-200'
      }`}>
        <opt.icon className={`h-7 w-7 transition-colors ${
          isActive ? 'text-[#2F80ED]' : 'text-slate-400 group-hover:text-slate-500'
        }`} />
      </div>
      <span className={`text-xs font-semibold mt-1.5 transition-colors ${
        isActive ? 'text-[#0A2540]' : 'text-slate-400'
      }`}>{opt.label}</span>
      {isActive && (
        <span className="text-[10px] font-bold text-[#2F80ED] mt-0.5">Selected</span>
      )}
    </button>
  );
}
